import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS } from '../../constants/theme.js';
import {
  getPropertyId,
  fetchFinancialStatement, fetchFinancialOutstanding,
  fetchFinancialComparison, fetchFinancialTax,
} from '../../services/api.js';
import { Card, Btn, SectionHeader, Spinner, Toast, useOfflineInvalidation } from '../../components/ui.jsx';
import { usePendingSync } from '../../hooks/usePendingSync.js';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtMoney = (n, currency) =>
  n != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0,
      }).format(n)
    : '—';

const fmtPct = (n, maxAbs = 999) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Math.abs(v) > maxAbs) return v > 0 ? `>+${maxAbs}%` : `>-${maxAbs}%`;
  return `${v.toFixed(1)}%`;
};

// Use local date strings throughout — toISOString() returns UTC which can give
// yesterday's date in UTC+N zones (e.g. Rwanda UTC+2 at 00:30 local = 22:30 UTC).
const _localDate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const today        = () => _localDate();
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return _localDate(d); };
const addDays      = (s, n) => {
  // Parse date string as local midnight to avoid UTC-offset shift
  const [y, m, day] = s.split('-').map(Number);
  const d = new Date(y, m - 1, day + n);
  return _localDate(d);
};
const arrowColor = (v) => (Number(v) >= 0 ? T.success : T.danger);
const ARROW      = (v) => (Number(v) >= 0 ? '↑' : '↓');

const METRIC_LABELS = {
  totalRevenue:     'Total Revenue',
  roomRevenue:      'Room Revenue',
  ancillaryRevenue: 'Other Charges',
  totalCollected:   'Total Collected',
  totalOutstanding: 'Outstanding Balance',
  taxCollected:     'Tax Collected',
  totalBookings:    'Total Bookings',
};

/* ─── KPI Card ───────────────────────────────────────────────────────────── */
function KpiCard({ icon, label, value, sub, trend, color }) {
  return (
    <Card style={{ padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -18, right: -18, fontSize: 60,
        opacity: .05, userSelect: 'none', pointerEvents: 'none' }}>{icon}</div>
      <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase',
        letterSpacing: .7, marginBottom: 10 }}>{label}</p>
      <p style={{ fontFamily: FONTS.display, fontSize: 26, fontWeight: 700,
        color: color || T.navy, lineHeight: 1 }}>{value}</p>
      {sub  && <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{sub}</p>}
      {trend != null && (
        <p style={{ fontSize: 12, color: arrowColor(trend), marginTop: 6, fontWeight: 600 }}>
          {ARROW(trend)} {fmtPct(Math.abs(Number(trend)), 999).replace('>-','>')} vs prior period
        </p>
      )}
    </Card>
  );
}

/* ─── Horizontal bar chart ───────────────────────────────────────────────── */
function MiniBar({ items, barColor, labelKey, valueKey, currency }) {
  if (!items || items.length === 0)
    return <p style={{ fontSize: 13, color: T.muted }}>No data for this period</p>;

  const max = Math.max(...items.map(i => Number(i[valueKey]) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, idx) => {
        const val = Number(item[valueKey]) || 0;
        const pct = item.pct != null ? item.pct : (val / max * 100);
        return (
          <div key={idx}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>{item[labelKey]}</span>
              <span style={{ fontSize: 12, color: T.navy, fontFamily: FONTS.mono }}>
                {fmtMoney(val, currency)} <span style={{ color: T.muted }}>({fmtPct(pct)})</span>
              </span>
            </div>
            <div style={{ background: `${barColor}20`, borderRadius: 4, height: 8, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, Number(pct) || 0)}%`, height: '100%',
                background: barColor, borderRadius: 4, transition: 'width .7s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Revenue vs Collections sparkline ──────────────────────────────────── */
function Sparkline({ data }) {
  if (!data || data.length === 0) return null;
  const W = 600, H = 80;
  const maxVal = Math.max(...data.flatMap(d => [Number(d.revenue) || 0, Number(d.collections) || 0]), 1);

  const line = (key) =>
    data.map((d, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * W;
      const y = H - ((Number(d[key]) || 0) / maxVal) * (H - 12) - 2;
      return `${x},${y}`;
    }).join(' ');

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 11 }}>
        <span style={{ color: T.teal, fontWeight: 700 }}>── Revenue</span>
        <span style={{ color: '#D4AF37', fontWeight: 700 }}>── Collections</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 80 }} preserveAspectRatio="none">
        <polyline fill="none" stroke={T.teal}    strokeWidth="2.5" points={line('revenue')} />
        <polyline fill="none" stroke="#D4AF37"   strokeWidth="2"   strokeDasharray="5 3" points={line('collections')} />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.muted, marginTop: 4 }}>
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

/* ─── Outstanding table ──────────────────────────────────────────────────── */
function OutstandingTable({ items, currency }) {
  if (!items || items.length === 0)
    return (
      <div style={{ textAlign: 'center', padding: '36px', color: T.success, fontSize: 14, fontWeight: 600 }}>
        ✅ No outstanding balances — all folios are settled
      </div>
    );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${T.border}` }}>
            {['Confirmation','Guest','Arrival','Departure','Charged','Paid','Balance','Status'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11,
                fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .5,
                whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.border}`,
              background: row.isOverdue ? `${T.danger}06` : 'transparent' }}>
              <td style={{ padding: '10px 12px', fontFamily: FONTS.mono, color: T.teal, fontSize: 12 }}>
                {row.confirmationNumber}
              </td>
              <td style={{ padding: '10px 12px', fontWeight: 600, color: T.navy }}>
                {row.guestName}
                {row.isOverdue && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: T.danger,
                    background: `${T.danger}15`, borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
                    OVERDUE
                  </span>
                )}
              </td>
              <td style={{ padding: '10px 12px', color: T.slate }}>{row.arrivalDate}</td>
              <td style={{ padding: '10px 12px', color: T.slate }}>{row.departureDate}</td>
              <td style={{ padding: '10px 12px', fontFamily: FONTS.mono }}>
                {fmtMoney(row.totalCharged, currency)}
              </td>
              <td style={{ padding: '10px 12px', fontFamily: FONTS.mono, color: T.success }}>
                {fmtMoney(row.totalPaid, currency)}
              </td>
              <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: FONTS.mono,
                color: row.isOverdue ? T.danger : T.navy }}>
                {fmtMoney(row.balance, currency)}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  color: T.muted, background: T.surface, borderRadius: 10, padding: '2px 8px' }}>
                  {row.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Comparison row ─────────────────────────────────────────────────────── */
function CompRow({ label, current, prior, change, changePct, currency, isCount }) {
  const fmt = (v) => (isCount ? Number(v).toLocaleString() : fmtMoney(v, currency));
  return (
    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
      <td style={{ padding: '10px 14px', fontSize: 13, color: T.navy, fontWeight: 600 }}>{label}</td>
      <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: FONTS.mono, textAlign: 'right' }}>{fmt(current)}</td>
      <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: FONTS.mono, color: T.muted, textAlign: 'right' }}>{fmt(prior)}</td>
      <td style={{ padding: '10px 14px', fontSize: 12, textAlign: 'right', fontWeight: 700,
        color: arrowColor(change) }}>
        {ARROW(change)} {fmtPct(changePct)}
      </td>
    </tr>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export default function FinancialStatement({ property }) {
  const [tab,     setTab]     = useState('statement');
  const [from,    setFrom]    = useState(firstOfMonth());
  const [to,      setTo]      = useState(today());
  const [data,    setData]    = useState({});
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState('');

  const { pendingCount, hasPending } = usePendingSync();
  const pid      = property?.id || getPropertyId();
  const currency = property?.currency || 'USD';
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      // All four calls use apiFetch → IndexedDB caching → automatic offline fallback
      const [stmt, outstanding, comparison, tax] = await Promise.all([
        fetchFinancialStatement(pid, from, to),
        fetchFinancialOutstanding(pid),
        fetchFinancialComparison(pid, from, to),
        fetchFinancialTax(pid, from, to),
      ]);
      const fromCache = stmt?._offline || outstanding?._offline;
      setData({ stmt, outstanding, comparison, tax });
      if (fromCache) showToast('📱 Showing cached data — changes sync when back online');
    } catch (e) {
      showToast(`❌ ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [pid, from, to]);

  useEffect(() => { load(); }, [load]);
  // 'reservations' fires on check-in/out and reservation create/update
  useOfflineInvalidation('reservations', load);
  // 'reports' fires when a folio charge or payment is posted offline
  // (deriveMutationInvalidation returns 'reservations dashboard reports' for charges/payments)
  useOfflineInvalidation('reports',      load);
  // 'dashboard' fires on virtually all offline mutations as a catch-all
  useOfflineInvalidation('dashboard',    load);
  // 'financial' kept for any future direct financial-specific events
  useOfflineInvalidation('financial',    load);

  const { stmt, outstanding, comparison, tax } = data;
  const kpi = stmt?.kpi || {};

  const TABS = [
    { id: 'statement',   label: '📊 P&L Statement' },
    { id: 'outstanding', label: '⏳ Outstanding' },
    { id: 'comparison',  label: '📈 Period Comparison' },
    { id: 'tax',         label: '🧾 Tax Summary' },
  ];

  return (
    <div className="fade-up">

      {/* ── Pending offline changes notice ── */}
      {hasPending && (
        <div style={{
          background:`${T.gold}14`, border:`1px solid ${T.gold}44`,
          borderRadius:10, padding:'9px 16px', marginBottom:14,
          display:'flex', alignItems:'center', gap:10, fontSize:12, color:'#92400e',
        }}>
          <span style={{ fontSize:16 }}>⏳</span>
          <span>
            <strong>{pendingCount} offline change{pendingCount > 1 ? 's' : ''} pending sync</strong> —
            financial figures below will update automatically once synchronisation completes.
          </span>
        </div>
      )}

      <SectionHeader
        title="Financial Statement"
        subtitle={`${property?.name || ''} · ${currency} · ${from} → ${to}`}
        right={
          <button onClick={() => window.print()} style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9,
            padding: '8px 16px', cursor: 'pointer', fontSize: 12, color: T.slate,
            fontFamily: FONTS.body, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
          }}>🖨️ Print Report</button>
        }
      />

      {/* Date range controls */}
      <Card style={{ padding: '16px 20px', marginBottom: 20, display: 'flex',
        gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, setter]) => (
          <label key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.muted,
              textTransform: 'uppercase', letterSpacing: .7 }}>{label}</span>
            <input type="date" value={val} onChange={e => setter(e.target.value)}
              style={{ border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 10px',
                fontSize: 13, color: T.navy, fontFamily: FONTS.body }} />
          </label>
        ))}
        {[
          ['This Month',   () => { setFrom(firstOfMonth());       setTo(today()); }],
          ['Last 7 Days',  () => { setFrom(addDays(today(), -7)); setTo(today()); }],
          ['Last 30 Days', () => { setFrom(addDays(today(),-30)); setTo(today()); }],
          ['Last 90 Days', () => { setFrom(addDays(today(),-90)); setTo(today()); }],
        ].map(([label, fn]) => (
          <button key={label} onClick={fn}
            style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7,
              padding: '8px 14px', fontSize: 12, color: T.slate, cursor: 'pointer',
              fontFamily: FONTS.body }}>
            {label}
          </button>
        ))}
        <Btn variant="primary" disabled={loading} onClick={load} style={{ marginLeft: 'auto' }}>
          {loading ? '…' : '🔄 Refresh'}
        </Btn>
      </Card>

      {/* Top KPI row */}
      {stmt && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
            <KpiCard icon="💰" label="Total Revenue"   value={fmtMoney(kpi.totalRevenue, currency)}   color={T.teal}
              sub={`${kpi.totalBookings || 0} bookings in period`}
              trend={comparison?.metrics?.find(m => m.metric === 'totalRevenue')?.changePct} />
            <KpiCard icon="✅" label="Total Collected"  value={fmtMoney(kpi.totalCollected, currency)}  color={T.success}
              sub={`${fmtPct(kpi.collectionRate)} collection rate`}
              trend={comparison?.metrics?.find(m => m.metric === 'totalCollected')?.changePct} />
            <KpiCard icon="⏳" label="Outstanding"      value={fmtMoney(outstanding?.summary?.totalOutstanding, currency)}
              color={Number(outstanding?.summary?.overdueAmount) > 0 ? T.danger : T.navy}
              sub={Number(outstanding?.summary?.overdueAmount) > 0
                ? `${fmtMoney(outstanding.summary.overdueAmount, currency)} overdue`
                : 'All balances current'} />
            <KpiCard icon="🧾" label="Tax Collected"    value={fmtMoney(kpi.taxCollected, currency)}
              sub={`${fmtPct(kpi.taxRate)} effective rate`} />
          </div>
          {/* Secondary KPI row: revenue breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
            <KpiCard icon="🛏️" label="Room Revenue"    value={fmtMoney(kpi.roomRevenue, currency)}    color="#5C8EE6"
              sub={`${fmtPct(kpi.roomRevenuePct)} of total revenue`} />
            <KpiCard icon="🍽️" label="Other Charges"   value={fmtMoney(kpi.ancillaryRevenue, currency)} color={T.warning}
              sub="F&B, extras, fees" />
            <KpiCard icon="📋" label="Total Bookings"  value={kpi.totalBookings || 0} color={T.navy}
              sub="Reservations in period" />
          </div>
        </>
      )}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: T.surface,
        borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ background: tab === t.id ? T.white : 'transparent', border: 'none',
              borderRadius: 8, padding: '8px 18px', fontSize: 13,
              fontWeight: tab === t.id ? 700 : 400,
              color: tab === t.id ? T.navy : T.muted,
              cursor: 'pointer', fontFamily: FONTS.body,
              boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
              transition: 'all .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>
      ) : (

        /* ── P&L Statement ──────────────────────────────────────────────── */
        tab === 'statement' && stmt ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

            <Card style={{ padding: 22 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 18 }}>Revenue by Category</p>
              <MiniBar
                items={(stmt.revenueBreakdown || []).map(d => ({ ...d, label: d.category }))}
                barColor={T.teal} labelKey="label" valueKey="amount" currency={currency}
              />
              <div style={{ borderTop: `2px solid ${T.border}`, marginTop: 16, paddingTop: 14,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Total Revenue</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 16, fontWeight: 700, color: T.teal }}>
                  {fmtMoney(kpi.totalRevenue, currency)}
                </span>
              </div>
            </Card>

            <Card style={{ padding: 22 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 18 }}>Collections by Payment Method</p>
              <MiniBar
                items={(stmt.paymentMethods || []).map(d => ({ ...d, label: d.method }))}
                barColor="#5C8EE6" labelKey="label" valueKey="amount" currency={currency}
              />
              <div style={{ borderTop: `2px solid ${T.border}`, marginTop: 16, paddingTop: 14,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Total Collected</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 16, fontWeight: 700, color: '#5C8EE6' }}>
                  {fmtMoney(kpi.totalCollected, currency)}
                </span>
              </div>
            </Card>

            {/* Daily trend full-width */}
            <Card style={{ padding: 22, gridColumn: '1 / -1' }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 16 }}>
                Daily Revenue vs Collections · {from} → {to}
              </p>
              <Sparkline data={stmt.dailyTrend} />
            </Card>

            {/* Income statement table full-width */}
            <Card style={{ padding: 22, gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, margin: 0 }}>Income Statement</p>
                <span style={{ fontSize: 11, color: T.muted }}>Period: {from} → {to} · {currency}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surface, borderBottom: `2px solid ${T.border}` }}>
                    {['Category', 'Amount', '% of Revenue', 'Indicator'].map(h => (
                      <th key={h} style={{ textAlign: h === 'Category' ? 'left' : 'right',
                        padding: '10px 14px', fontSize: 10, fontWeight: 700, color: T.muted,
                        textTransform: 'uppercase', letterSpacing: .6 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(stmt.revenueBreakdown || []).map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surface}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '11px 14px', fontWeight: 600, color: T.navy }}>{row.category}</td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: FONTS.mono, color: T.navy }}>
                        {fmtMoney(row.amount, currency)}
                      </td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', color: T.muted, fontWeight: 600 }}>{fmtPct(row.pct)}</td>
                      <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-block', height: 8, borderRadius: 4,
                          width: `${Math.max(4, Math.min(100, Number(row.pct) || 0))}px`,
                          background: T.teal, opacity: .7 }} />
                      </td>
                    </tr>
                  ))}
                  {/* Totals section */}
                  <tr style={{ borderTop: `2px solid ${T.border}`, background: T.surface }}>
                    <td style={{ padding: '12px 14px', fontWeight: 800, color: T.teal, fontSize: 14 }}>Total Revenue</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: FONTS.mono,
                      fontWeight: 800, fontSize: 15, color: T.teal }}>{fmtMoney(kpi.totalRevenue, currency)}</td>
                    <td colSpan={2} />
                  </tr>
                  {[
                    { label: '  Room Revenue',     val: kpi.roomRevenue,      cls: '#5C8EE6', indent: true },
                    { label: '  Other Charges',    val: kpi.ancillaryRevenue, cls: T.warning, indent: true },
                    { label: 'Total Collected',    val: kpi.totalCollected,   cls: T.success },
                    { label: 'Outstanding Balance',val: kpi.totalOutstanding, cls: T.danger  },
                    { label: 'Tax Collected',      val: kpi.taxCollected,     cls: T.muted   },
                    { label: 'Net Income (est.)',   val: (kpi.totalRevenue||0) - (kpi.taxCollected||0), cls: T.navy },
                  ].map(({ label, val, cls, indent }) => (
                    <tr key={label} style={{ borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surface}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px', fontWeight: indent ? 500 : 700, color: T.navy,
                        paddingLeft: indent ? 28 : 14, fontSize: indent ? 12 : 13 }}>{label}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: FONTS.mono,
                        fontWeight: indent ? 400 : 600, color: cls }}>{fmtMoney(val, currency)}</td>
                      <td colSpan={2} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )

        /* ── Outstanding Balances ────────────────────────────────────────── */
        : tab === 'outstanding' && outstanding ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <KpiCard icon="📋" label="Total Outstanding"
                value={fmtMoney(outstanding.summary?.totalOutstanding, currency)}
                color={Number(outstanding.summary?.overdueAmount) > 0 ? T.danger : T.navy} />
              <KpiCard icon="🚨" label="Overdue Balances"
                value={fmtMoney(outstanding.summary?.overdueAmount, currency)}
                color={Number(outstanding.summary?.overdueAmount) > 0 ? T.danger : T.muted}
                sub="Checked-out with unpaid balance" />
              <KpiCard icon="📄" label="Open Folios"
                value={outstanding.summary?.count ?? 0}
                sub="Reservations with balance due" />
              <KpiCard icon="✅" label="Fully Settled"
                value={outstanding.summary?.settledCount ?? '—'}
                color={T.success}
                sub="Paid in full" />
            </div>
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
                  Outstanding Folios <span style={{ color: T.muted, fontWeight: 400, fontSize: 12 }}>as of {outstanding.asOf}</span>
                </p>
                {outstanding.summary?.overdueAmount > 0 && (
                  <span style={{ fontSize: 12, color: T.danger, fontWeight: 600,
                    background: `${T.danger}12`, borderRadius: 20, padding: '4px 12px' }}>
                    ⚠ Overdue items need attention
                  </span>
                )}
              </div>
              <OutstandingTable items={outstanding.items} currency={currency} />
            </Card>
          </div>
        )

        /* ── Period Comparison ───────────────────────────────────────────── */
        : tab === 'comparison' && comparison ? (
          <Card style={{ padding: 22 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 6 }}>Period Comparison</p>
            <p style={{ fontSize: 12, color: T.muted, marginBottom: 20 }}>
              Current period: <strong>{comparison.currentFrom} → {comparison.currentTo}</strong>
              {' '}vs Prior period: <strong>{comparison.priorFrom} → {comparison.priorTo}</strong>
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {['Metric', 'Current Period', 'Prior Period', 'Change'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Metric' ? 'left' : 'right',
                      padding: '8px 14px', fontSize: 11, fontWeight: 700, color: T.muted,
                      textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(comparison.metrics || []).map((m, i) => (
                  <CompRow key={i}
                    label={METRIC_LABELS[m.metric] || m.metric}
                    current={m.current} prior={m.prior}
                    change={m.change}   changePct={m.changePct}
                    currency={currency}
                    isCount={m.metric === 'totalBookings'}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        )

        /* ── Tax Summary ─────────────────────────────────────────────────── */
        : tab === 'tax' && tax ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <KpiCard icon="🧾" label="Taxable Revenue" value={fmtMoney(tax.totalTaxable, currency)} />
              <KpiCard icon="💸" label="Tax Collected"   value={fmtMoney(tax.totalTax, currency)} color={T.teal} />
              <KpiCard icon="📊" label="Effective Rate"
                value={tax.totalTaxable && tax.totalTax ? fmtPct((tax.totalTax / tax.totalTaxable) * 100) : '—'}
                color={T.navy} sub="Avg across all charges" />
              <KpiCard icon="📋" label="Tax Lines"
                value={tax.breakdown?.length ?? 0}
                color={T.muted} sub="Distinct tax codes" />
            </div>
            <Card style={{ padding: 22 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 16 }}>
                Tax Breakdown by Code
              </p>
              {!tax.breakdown?.length ? (
                <p style={{ fontSize: 13, color: T.muted, padding: '24px 0', textAlign: 'center' }}>
                  No tax records for this period. Ensure tax codes are set on your folio charge types.
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                      {['Tax Code', 'Taxable Amount', 'Tax Collected', 'Effective Rate'].map(h => (
                        <th key={h} style={{ textAlign: h === 'Tax Code' ? 'left' : 'right',
                          padding: '8px 14px', fontSize: 11, fontWeight: 700, color: T.muted,
                          textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tax.breakdown.map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: T.navy }}>{row.taxCode}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: FONTS.mono }}>
                          {fmtMoney(row.taxableAmount, currency)}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: FONTS.mono, color: T.teal }}>
                          {fmtMoney(row.taxAmount, currency)}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: T.muted, fontWeight: 600 }}>
                          {fmtPct(row.effectiveRate)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${T.border}`, background: T.surface }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: T.navy }}>Total</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: FONTS.mono, fontWeight: 700 }}>
                        {fmtMoney(tax.totalTaxable, currency)}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: FONTS.mono,
                        fontWeight: 700, color: T.teal }}>
                        {fmtMoney(tax.totalTax, currency)}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: T.muted, fontSize: 13 }}>
            No data available. Try adjusting the date range and refreshing.
          </div>
        )
      )}

      <Toast message={toast} />
    </div>
  );
}